import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { INR } from "@/lib/constants";
import { Plus, X, Trash2, Upload, Save, GripVertical } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const inputCls = "w-full px-3 py-2 bg-white border border-[#EAE7E0] text-sm text-[#2A2421] focus:border-[#C88270] focus:outline-none";
const labelCls = "block text-[10px] uppercase tracking-[0.2em] text-[#5C5652] mb-1.5";

function Section({ title, children }) {
  return (
    <div className="bg-white border border-[#EAE7E0] p-6 space-y-4">
      <div className="font-serif text-xl text-[#2A2421]">{title}</div>
      {children}
    </div>
  );
}

export default function QuoteSettings({ settings, onChanged }) {
  const [s, setS] = useState(settings);
  const [newType, setNewType] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragSec, setDragSec] = useState(null);

  useEffect(() => { setS(settings); }, [settings]);
  if (!s) return null;

  const set = (patch) => setS((p) => ({ ...p, ...patch }));
  const setStudio = (patch) => set({ studio: { ...s.studio, ...patch } });
  const setSocial = (patch) => set({ socials: { ...s.socials, ...patch } });

  const saveText = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/quote-settings", {
        studio: s.studio, terms: s.terms, inclusions: s.inclusions, cancellation: s.cancellation,
        reviews: s.reviews, google_rating: Number(s.google_rating) || 0, google_review_count: Number(s.google_review_count) || 0,
        socials: s.socials, services: s.services, about: s.about,
        why_choose_us: s.why_choose_us, faqs: s.faqs,
        section_order: s.section_order, instagram: { url: s.instagram?.url || "", handle: s.instagram?.handle || "" },
        advance_default_type: s.advance_default_type, advance_default_value: Number(s.advance_default_value) || 0,
      });
      setS(data); onChanged?.(data);
      toast.success("Settings saved");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const addType = async () => {
    const name = newType.trim();
    if (!name) return;
    try { const { data } = await api.post("/quote-settings/event-types", { name }); setS(data); onChanged?.(data); setNewType(""); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const removeType = async (name) => {
    try { const { data } = await api.delete(`/quote-settings/event-types/${encodeURIComponent(name)}`); setS(data); onChanged?.(data); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const refresh = async () => { const { data } = await api.get("/quote-settings"); setS(data); onChanged?.(data); };
  const addGallery = async () => { try { const { data } = await api.post("/quote-settings/galleries", { header: "New Gallery" }); setS(data); onChanged?.(data); } catch (err) { toast.error(formatApiError(err)); } };
  const renameGallery = async (gid, header) => { try { await api.put(`/quote-settings/galleries/${gid}`, { header }); } catch (err) { toast.error(formatApiError(err)); } };
  const deleteGallery = async (gid) => { try { const { data } = await api.delete(`/quote-settings/galleries/${gid}`); setS(data); onChanged?.(data); } catch (err) { toast.error(formatApiError(err)); } };
  const uploadGalleryImage = async (gid, e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(gid);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/quote-settings/galleries/${gid}/images`, fd, { headers: { "Content-Type": "multipart/form-data" } }); await refresh(); toast.success("Image uploaded"); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(null); e.target.value = ""; }
  };
  const deleteGalleryImage = async (gid, imgId) => { try { await api.delete(`/quote-settings/galleries/${gid}/images/${imgId}`); await refresh(); } catch (err) { toast.error(formatApiError(err)); } };
  const setGalleryHeader = (gid, header) => set({ galleries: s.galleries.map((g) => (g.id === gid ? { ...g, header } : g)) });

  const uploadInstagram = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post("/quote-settings/instagram-screenshot", fd, { headers: { "Content-Type": "multipart/form-data" } }); await refresh(); toast.success("Instagram screenshot updated"); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { e.target.value = ""; }
  };
  const setInsta = (patch) => set({ instagram: { ...s.instagram, ...patch } });

  const SECTION_LABELS = { about: "Meet the Artist", galleries: "Portfolio Galleries", why_choose_us: "Why Choose Us?", instagram: "Instagram", pricing: "Pricing & Events", faqs: "FAQ", reviews: "Reviews", terms: "Terms & Inclusions" };
  const sectionLabel = (key) => {
    if (typeof key === "string" && key.startsWith("gallery:")) {
      const g = (s.galleries || []).find((x) => x.id === key.split(":")[1]);
      return `Gallery — ${g?.header || "Untitled"}`;
    }
    return SECTION_LABELS[key] || key;
  };
  const moveSection = (from, to) => {
    if (from == null || to == null || from === to) return;
    const arr = [...(s.section_order || [])]; const [m] = arr.splice(from, 1); arr.splice(to, 0, m); set({ section_order: arr });
  };

  const uploadArtist = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/quote-settings/artist-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Artist photo updated");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { e.target.value = ""; }
  };

  const uploadEnquiryBg = async (variant, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/quote-settings/enquiry-bg/${variant}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Enquiry background updated");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { e.target.value = ""; }
  };
  const removeEnquiryBg = async (variant) => {
    try { await api.delete(`/quote-settings/enquiry-bg/${variant}`); await refresh(); toast.success("Background removed"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const addService = () => set({ services: [...(s.services || []), { id: `${Date.now()}`, name: "New service", direct_price: 0, partner_price: 0 }] });
  const addReview = () => set({ reviews: [...(s.reviews || []), { name: "", text: "", rating: 5 }] });
  const setReview = (i, patch) => set({ reviews: s.reviews.map((r, idx) => (idx === i ? { ...r, ...patch } : r)) });

  const why = s.why_choose_us || { header: "Why Choose Us?", items: [] };
  const setWhyHeader = (header) => set({ why_choose_us: { ...why, header } });
  const addWhyItem = () => set({ why_choose_us: { ...why, items: [...(why.items || []), { id: `w${Date.now()}`, text: "" }] } });
  const setWhyItem = (id, text) => set({ why_choose_us: { ...why, items: why.items.map((it) => (it.id === id ? { ...it, text } : it)) } });
  const delWhyItem = (id) => set({ why_choose_us: { ...why, items: why.items.filter((it) => it.id !== id) } });

  const faq = s.faqs || { header: "Frequently Asked Questions", items: [] };
  const setFaqHeader = (header) => set({ faqs: { ...faq, header } });
  const addFaq = () => set({ faqs: { ...faq, items: [...(faq.items || []), { id: `f${Date.now()}`, question: "", answer: "" }] } });
  const setFaq = (id, patch) => set({ faqs: { ...faq, items: faq.items.map((it) => (it.id === id ? { ...it, ...patch } : it)) } });
  const delFaq = (id) => set({ faqs: { ...faq, items: faq.items.filter((it) => it.id !== id) } });

  return (
    <div className="space-y-6" data-testid="quote-settings">
      <div className="flex justify-end sticky top-16 md:top-4 z-10">
        <button onClick={saveText} disabled={saving} data-testid="settings-save"
          className="inline-flex items-center gap-2 px-6 py-2.5 text-xs uppercase tracking-[0.2em] bg-[#2A2421] text-white hover:bg-[#C88270] transition-colors disabled:opacity-50">
          <Save size={14} /> {saving ? "Saving…" : "Save Settings"}
        </button>
      </div>

      <Section title="Event Types">
        <p className="text-xs text-[#5C5652]">Fixed once here — add or remove as needed. These appear in the quote builder.</p>
        <div className="flex flex-wrap gap-2">
          {(s.event_types || []).map((t) => (
            <span key={t} data-testid={`type-chip-${t}`} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-[#FAF9F6] border border-[#EAE7E0] text-[#2A2421]">
              {t}<button onClick={() => removeType(t)} className="text-[#5C5652] hover:text-red-600"><X size={14} /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-2 max-w-md">
          <input value={newType} onChange={(e) => setNewType(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addType()} data-testid="new-type-input" placeholder="Add event type…" className={inputCls} />
          <button onClick={addType} data-testid="add-type-btn" className="px-4 py-2 bg-[#C88270] text-white text-xs uppercase tracking-[0.15em] hover:bg-[#2A2421] transition-colors"><Plus size={16} /></button>
        </div>
      </Section>

      <Section title="Service Catalog & Pricing">
        <p className="text-xs text-[#5C5652]">Default prices for quick-add in the builder. Direct vs Partner rate.</p>
        <div className="space-y-2">
          <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-[0.2em] text-[#5C5652]">
            <div className="col-span-6">Service</div><div className="col-span-3">Direct ₹</div><div className="col-span-3">Partner ₹</div>
          </div>
          {(s.services || []).map((sv, i) => (
            <div key={sv.id} className="grid grid-cols-12 gap-2 items-center" data-testid={`service-row-${i}`}>
              <input value={sv.name} onChange={(e) => setService(i, { name: e.target.value })} className={`${inputCls} col-span-6`} />
              <input type="number" value={sv.direct_price} onChange={(e) => setService(i, { direct_price: Number(e.target.value) })} className={`${inputCls} col-span-3`} />
              <div className="col-span-3 flex items-center gap-1">
                <input type="number" value={sv.partner_price} onChange={(e) => setService(i, { partner_price: Number(e.target.value) })} className={inputCls} />
                <button onClick={() => set({ services: s.services.filter((_, idx) => idx !== i) })} className="p-1 text-[#5C5652] hover:text-red-600"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
        <button onClick={addService} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-[#C88270] hover:text-[#2A2421]"><Plus size={14} /> Add service</button>
      </Section>

      <Section title="About the Artist (Vinni)">
        <div className="flex flex-col sm:flex-row gap-5">
          <div className="shrink-0">
            <div className="w-32 h-40 bg-[#FAF9F6] border border-[#EAE7E0] overflow-hidden">
              {s.artist_photo ? (
                <img src={`${BACKEND_URL}/api/public/portfolio/${s.artist_photo.id}`} alt="Vinni" className="w-full h-full object-cover" data-testid="artist-photo-img" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[10px] uppercase tracking-[0.15em] text-[#5C5652]">No photo</div>
              )}
            </div>
            <label className="mt-2 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-[#C88270] hover:text-[#2A2421] cursor-pointer" data-testid="artist-upload-label">
              <Upload size={14} /> Upload photo
              <input type="file" accept="image/*" onChange={uploadArtist} className="hidden" data-testid="artist-upload-input" />
            </label>
          </div>
          <div className="flex-1">
            <label className={labelCls}>About Vinni (shown on the quote & link)</label>
            <textarea value={s.about || ""} onChange={(e) => set({ about: e.target.value })} rows={6} data-testid="about-textarea" placeholder="Tell clients about Vinni's artistry, experience and signature style…" className={inputCls} />
          </div>
        </div>
      </Section>

      <Section title="Enquiry Page Background">
        <p className="text-xs text-[#5C5652]">Background photos for your public enquiry form (/enquiry). Upload a landscape image for web and a portrait image for mobile. Leave empty to use the built-in defaults.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[["web", "Web (landscape)", "aspect-[16/9]"], ["mobile", "Mobile (portrait)", "aspect-[3/4] max-w-[220px]"]].map(([variant, label, aspect]) => {
            const img = s.enquiry_bg?.[variant];
            return (
              <div key={variant} className="space-y-2" data-testid={`enquiry-bg-${variant}`}>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5652]">{label}</div>
                <div className={`${aspect} bg-[#FAF9F6] border border-[#EAE7E0] overflow-hidden flex items-center justify-center`}>
                  {img ? (
                    <img src={`${BACKEND_URL}/api/public/portfolio/${img.id}?w=800`} alt={label} className="w-full h-full object-cover" data-testid={`enquiry-bg-img-${variant}`} />
                  ) : (
                    <span className="text-[10px] text-[#B8B2AA] uppercase tracking-[0.15em]">Using default</span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <label className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-[#C88270] hover:text-[#2A2421] cursor-pointer" data-testid={`enquiry-bg-upload-${variant}`}>
                    <Upload size={14} /> Upload
                    <input type="file" accept="image/*" onChange={(e) => uploadEnquiryBg(variant, e)} className="hidden" />
                  </label>
                  {img && (
                    <button onClick={() => removeEnquiryBg(variant)} data-testid={`enquiry-bg-remove-${variant}`} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-[#5C5652] hover:text-red-600">
                      <Trash2 size={13} /> Remove
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      <Section title="Quotation Layout — Section Order">
        <p className="text-xs text-[#5C5652]">Drag to reorder how sections appear on the PDF and the public link.</p>
        <div className="space-y-2 max-w-md" data-testid="section-order-list">
          {(s.section_order || []).map((key, i) => (key === "about" ? null : (
            <div key={key} draggable onDragStart={() => setDragSec(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => { moveSection(dragSec, i); setDragSec(null); }}
              data-testid={`section-order-${key}`}
              className={`flex items-center gap-3 px-4 py-3 bg-white border cursor-grab ${dragSec === i ? "border-[#C88270] opacity-60" : "border-[#EAE7E0]"}`}>
              <GripVertical size={16} className="text-[#B8B2AA]" />
              <span className="text-[10px] text-[#5C5652] w-5">{i + 1}</span>
              <span className="text-sm text-[#2A2421]">{sectionLabel(key)}</span>
            </div>
          )))}
        </div>
      </Section>

      <Section title="Portfolio Galleries">
        <p className="text-xs text-[#5C5652]">Create multiple galleries — each with its own header and set of images. Shown on the PDF & link.</p>
        <div className="space-y-6">
          {(s.galleries || []).map((g) => (
            <div key={g.id} className="border border-[#EAE7E0] p-4" data-testid={`gallery-${g.id}`}>
              <div className="flex items-center gap-2 mb-3">
                <input value={g.header} onChange={(e) => setGalleryHeader(g.id, e.target.value)} onBlur={(e) => renameGallery(g.id, e.target.value)} data-testid={`gallery-header-${g.id}`} placeholder="Gallery header (e.g. Bridal Looks)" className={inputCls} />
                <button onClick={() => deleteGallery(g.id)} data-testid={`gallery-delete-${g.id}`} className="p-2 text-[#5C5652] hover:text-red-600 shrink-0"><Trash2 size={16} /></button>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                {(g.images || []).filter((p) => !p.is_deleted).map((p) => (
                  <div key={p.id} className="relative group aspect-[4/3] bg-[#FAF9F6] border border-[#EAE7E0] overflow-hidden" data-testid={`gallery-img-${p.id}`}>
                    <img src={`${BACKEND_URL}/api/public/portfolio/${p.id}`} alt="portfolio" className="w-full h-full object-cover" />
                    <button onClick={() => deleteGalleryImage(g.id, p.id)} data-testid={`gallery-img-delete-${p.id}`} className="absolute top-1 right-1 p-1.5 bg-white/90 text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
                  </div>
                ))}
                <label className="aspect-[4/3] border border-dashed border-[#C88270] flex flex-col items-center justify-center gap-1 cursor-pointer text-[#C88270] hover:bg-[#C88270]/5" data-testid={`gallery-upload-${g.id}`}>
                  <Upload size={18} />
                  <span className="text-[10px] uppercase tracking-[0.15em]">{uploading === g.id ? "Uploading…" : "Upload"}</span>
                  <input type="file" accept="image/*" onChange={(e) => uploadGalleryImage(g.id, e)} className="hidden" />
                </label>
              </div>
            </div>
          ))}
        </div>
        <button onClick={addGallery} data-testid="add-gallery-btn" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-[#C88270] hover:text-[#2A2421]"><Plus size={14} /> Add gallery</button>
      </Section>

      <Section title="Instagram Highlight">
        <p className="text-xs text-[#5C5652]">Upload a screenshot of your Instagram page — shown in an exclusive section with a Follow button.</p>
        <div className="flex flex-col sm:flex-row gap-5">
          <div className="shrink-0">
            <div className="w-40 h-48 bg-[#FAF9F6] border border-[#EAE7E0] overflow-hidden">
              {s.instagram?.screenshot ? (
                <img src={`${BACKEND_URL}/api/public/portfolio/${s.instagram.screenshot.id}`} alt="instagram" className="w-full h-full object-cover" data-testid="insta-shot-img" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[10px] uppercase tracking-[0.15em] text-[#5C5652]">No screenshot</div>
              )}
            </div>
            <label className="mt-2 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-[#C88270] hover:text-[#2A2421] cursor-pointer" data-testid="insta-upload-label">
              <Upload size={14} /> Upload screenshot
              <input type="file" accept="image/*" onChange={uploadInstagram} className="hidden" data-testid="insta-upload-input" />
            </label>
          </div>
          <div className="flex-1 space-y-3">
            <div><label className={labelCls}>Instagram handle</label><input value={s.instagram?.handle || ""} onChange={(e) => setInsta({ handle: e.target.value })} data-testid="insta-handle" placeholder="@dejavumakeupbyvinni" className={inputCls} /></div>
            <div><label className={labelCls}>Profile URL</label><input value={s.instagram?.url || ""} onChange={(e) => setInsta({ url: e.target.value })} data-testid="insta-url" placeholder="https://www.instagram.com/dejavumakeupbyvinni/" className={inputCls} /></div>
          </div>
        </div>
      </Section>

      <Section title="Why Choose Us?">
        <p className="text-xs text-[#5C5652]">A benefits list shown as a designed section on the PDF & link. Fully editable — add as many points as you like.</p>
        <div><label className={labelCls}>Section header</label>
          <input value={why.header || ""} onChange={(e) => setWhyHeader(e.target.value)} data-testid="why-header" placeholder="Why Choose Us?" className={inputCls} />
        </div>
        <div className="space-y-2">
          {(why.items || []).map((it) => (
            <div key={it.id} className="flex items-center gap-2" data-testid={`why-item-${it.id}`}>
              <input value={it.text} onChange={(e) => setWhyItem(it.id, e.target.value)} placeholder="e.g. Free Makeup Trials Inclusive in Package" className={inputCls} />
              <button onClick={() => delWhyItem(it.id)} data-testid={`why-del-${it.id}`} className="p-2 text-[#5C5652] hover:text-red-600 shrink-0"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
        <button onClick={addWhyItem} data-testid="why-add-btn" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-[#C88270] hover:text-[#2A2421]"><Plus size={14} /> Add point</button>
      </Section>

      <Section title="FAQ">
        <p className="text-xs text-[#5C5652]">Questions & answers shown in a clean section on the PDF & link. Add as many as you need.</p>
        <div><label className={labelCls}>Section header</label>
          <input value={faq.header || ""} onChange={(e) => setFaqHeader(e.target.value)} data-testid="faq-header" placeholder="Frequently Asked Questions" className={inputCls} />
        </div>
        <div className="space-y-4">
          {(faq.items || []).map((it) => (
            <div key={it.id} className="border border-[#EAE7E0] p-4 space-y-2" data-testid={`faq-item-${it.id}`}>
              <div className="flex items-center gap-2">
                <input value={it.question} onChange={(e) => setFaq(it.id, { question: e.target.value })} data-testid={`faq-q-${it.id}`} placeholder="Question" className={`${inputCls} font-medium`} />
                <button onClick={() => delFaq(it.id)} data-testid={`faq-del-${it.id}`} className="p-2 text-[#5C5652] hover:text-red-600 shrink-0"><Trash2 size={15} /></button>
              </div>
              <textarea value={it.answer} onChange={(e) => setFaq(it.id, { answer: e.target.value })} data-testid={`faq-a-${it.id}`} rows={2} placeholder="Answer" className={inputCls} />
            </div>
          ))}
        </div>
        <button onClick={addFaq} data-testid="faq-add-btn" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-[#C88270] hover:text-[#2A2421]"><Plus size={14} /> Add FAQ</button>
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title="Social Proof">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Google rating</label><input type="number" step="0.1" value={s.google_rating} onChange={(e) => set({ google_rating: e.target.value })} data-testid="google-rating" className={inputCls} /></div>
            <div><label className={labelCls}>Google reviews #</label><input type="number" value={s.google_review_count} onChange={(e) => set({ google_review_count: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>Instagram handle</label><input value={s.socials?.instagram_handle || ""} onChange={(e) => setSocial({ instagram_handle: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>Instagram followers</label><input value={s.socials?.instagram_followers || ""} onChange={(e) => setSocial({ instagram_followers: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>Weddings done</label><input value={s.socials?.weddings_done || ""} onChange={(e) => setSocial({ weddings_done: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>Years experience</label><input value={s.socials?.years_experience || ""} onChange={(e) => setSocial({ years_experience: e.target.value })} className={inputCls} /></div>
          </div>
        </Section>

        <Section title="Advance Default & Studio">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Advance type</label>
              <select value={s.advance_default_type} onChange={(e) => set({ advance_default_type: e.target.value })} className={inputCls}>
                <option value="percent">Percent %</option><option value="fixed">Fixed ₹</option>
              </select>
            </div>
            <div><label className={labelCls}>Advance value</label><input type="number" value={s.advance_default_value} onChange={(e) => set({ advance_default_value: e.target.value })} className={inputCls} /></div>
          </div>
          <div><label className={labelCls}>Studio name</label><input value={s.studio?.name || ""} onChange={(e) => setStudio({ name: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Tagline</label><input value={s.studio?.tagline || ""} onChange={(e) => setStudio({ tagline: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Address</label><input value={s.studio?.address || ""} onChange={(e) => setStudio({ address: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Phone</label><input value={s.studio?.phone || ""} onChange={(e) => setStudio({ phone: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Payment link (Razorpay / UPI)</label><input value={s.studio?.payment_link || ""} onChange={(e) => setStudio({ payment_link: e.target.value })} data-testid="studio-payment-link" placeholder="https://razorpay.me/@dejavumakeupbyvinni" className={inputCls} /><p className="text-[10px] text-[#5C5652] mt-1">Powers the "Reserve my date" button on the public quote link.</p></div>
        </Section>
      </div>

      <Section title="Reviews">
        {(s.reviews || []).map((r, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-start" data-testid={`review-${i}`}>
            <input value={r.name} onChange={(e) => setReview(i, { name: e.target.value })} placeholder="Name" className={`${inputCls} col-span-3`} />
            <input value={r.text} onChange={(e) => setReview(i, { text: e.target.value })} placeholder="Review text" className={`${inputCls} col-span-7`} />
            <input type="number" min="1" max="5" value={r.rating} onChange={(e) => setReview(i, { rating: Number(e.target.value) })} className={`${inputCls} col-span-1`} />
            <button onClick={() => set({ reviews: s.reviews.filter((_, idx) => idx !== i) })} className="col-span-1 p-2 text-[#5C5652] hover:text-red-600"><Trash2 size={15} /></button>
          </div>
        ))}
        <button onClick={addReview} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-[#C88270] hover:text-[#2A2421]"><Plus size={14} /> Add review</button>
      </Section>

      <Section title="Terms, Inclusions & Cancellation">
        <div><label className={labelCls}>What's included</label><textarea value={s.inclusions || ""} onChange={(e) => set({ inclusions: e.target.value })} rows={3} className={inputCls} /></div>
        <div><label className={labelCls}>Terms & Conditions</label><textarea value={s.terms || ""} onChange={(e) => set({ terms: e.target.value })} rows={4} className={inputCls} /></div>
        <div><label className={labelCls}>Cancellation & Refund</label><textarea value={s.cancellation || ""} onChange={(e) => set({ cancellation: e.target.value })} rows={3} className={inputCls} /></div>
      </Section>
    </div>
  );
}
